import React, { useState } from 'react';

const getShapeStyle = (shape) => {
  const shapeStyles = {
    circle: { borderRadius: '50%' },
    square: { borderRadius: '4px' },
    hexagon: { 
      clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)'
    },
    pentagon: { 
      clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)'
    },
    star: { 
      clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
    },
    'rounded-lg': { borderRadius: '12px' },
  };
  return shapeStyles[shape] || shapeStyles.circle;
};

export default function TechnologyStackSection() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('Frontend & Web');

  const borderColors = [
    '#FF6B6B', '#FF8C42', '#FFD166', '#90BE6D', '#577590',
    '#F72585', '#B5179E', '#7209B7', '#3A0CA3', '#560BAD',
    '#FF006E', '#FB5607', '#FFBE0B', '#8338EC', '#3A86FF',
    '#06FFA5', '#FF006E', '#FB5607', '#FFBE0B', '#8338EC',
    '#3A86FF', '#06FFA5'
  ];

  // Generate random animation values for each tech
  const getRandomAnimation = (idx) => {
    const duration = 6 + (idx % 8);
    const randomOffset = idx * 0.2;
    return { duration, delay: randomOffset };
  };

  const technologies = [
    // Row 1 (Top) - 20% gaps horizontally
    { name: "HTML5", logo: "https://cdn.simpleicons.org/html5/FF6B6B", x: "5%", y: "0%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[0], category: "Frontend & Web" },
    { name: "CSS3", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/css3/css3-original.svg", x: "25%", y: "2%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[1], category: "Frontend & Web" },
    { name: "JavaScript", logo: "https://cdn.simpleicons.org/javascript/f7df1e", x: "45%", y: "0%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[2], category: "Frontend & Web" },
    { name: "TypeScript", logo: "https://cdn.simpleicons.org/typescript/3178c6", x: "65%", y: "3%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[3], category: "Frontend & Web" },
    { name: "React", logo: "https://cdn.simpleicons.org/react/61dafb", x: "85%", y: "1%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[4], category: "Frontend & Web" },
    
    // Row 2 - 18% vertical gap from row 1
    { name: "Next.js", logo: "https://cdn.simpleicons.org/nextdotjs/000000", x: "15%", y: "18%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[5], category: "Frontend & Web" },
    { name: "Vue.js", logo: "https://cdn.simpleicons.org/vuedotjs/4fc08d", x: "35%", y: "20%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[8], category: "Frontend & Web" },
    { name: "Angular", logo: "https://cdn.simpleicons.org/angular/dd0031", x: "55%", y: "18%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[9], category: "Frontend & Web" },
    { name: "Tailwind CSS", logo: "https://cdn.simpleicons.org/tailwindcss/06b6d4", x: "75%", y: "20%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[10], category: "Frontend & Web" },
    
    // Row 3 - 18% vertical gap
    { name: "Node.js", logo: "https://cdn.simpleicons.org/nodedotjs/339933", x: "5%", y: "36%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[6], category: "Backend & Databases" },
    { name: "Express", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/express/express-original.svg", x: "25%", y: "38%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[21], category: "Backend & Databases" },
    { name: "Python", logo: "https://cdn.simpleicons.org/python/3776ab", x: "45%", y: "36%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[7], category: "Backend & Databases" },
    { name: "PostgreSQL", logo: "https://cdn.simpleicons.org/postgresql/336791", x: "65%", y: "38%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[19], category: "Backend & Databases" },
    { name: "MongoDB", logo: "https://cdn.simpleicons.org/mongodb/13aa52", x: "85%", y: "36%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[20], category: "Backend & Databases" },
    
    // Row 4 - 18% vertical gap
    { name: "Redis", logo: "https://cdn.simpleicons.org/redis/dc382d", x: "15%", y: "54%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[13], category: "Backend & Databases" },
    { name: "Docker", logo: "https://cdn.simpleicons.org/docker/2496ed", x: "35%", y: "56%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[18], category: "DevOps & Cloud" },
    { name: "Kubernetes", logo: "https://cdn.simpleicons.org/kubernetes/326ce3", x: "55%", y: "54%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[15], category: "DevOps & Cloud" },
    { name: "AWS", logo: "https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/amazonaws.svg", x: "75%", y: "56%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[17], category: "DevOps & Cloud" },
    
    // Row 5 - 18% vertical gap
    { name: "GCP", logo: "https://cdn.simpleicons.org/googlecloud/4285f4", x: "5%", y: "72%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[16], category: "DevOps & Cloud" },
    { name: "Linux", logo: "https://cdn.simpleicons.org/linux/fcc624", x: "25%", y: "74%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[15], category: "DevOps & Cloud" },
    { name: "Nginx", logo: "https://cdn.simpleicons.org/nginx/009639", x: "45%", y: "72%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[14], category: "DevOps & Cloud" },
    
    // Row 6 (scattered/remaining)
    { name: "Git", logo: "https://cdn.simpleicons.org/git/f1502f", x: "65%", y: "74%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[11], category: "Tools & Version Control" },
    { name: "GitHub", logo: "https://cdn.simpleicons.org/github/181717", x: "85%", y: "72%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[2], category: "Tools & Version Control" },
    { name: "GitLab", logo: "https://cdn.simpleicons.org/gitlab/fc6d26", x: "35%", y: "8%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[0], category: "Tools & Version Control" },
    { name: "Figma", logo: "https://cdn.simpleicons.org/figma/f24e1e", x: "75%", y: "8%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[12], category: "Design & UI" },
    { name: "Adobe XD", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/xd/xd-original.svg", x: "15%", y: "88%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[4], category: "Design & UI" },
    { name: "Sketch", logo: "https://cdn.simpleicons.org/sketch/f3a200", x: "55%", y: "88%", size: "w-16 h-16", shape: "circle", borderColor: borderColors[3], category: "Design & UI" },
  ];

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <section className="py-20 lg:py-32 bg-gray-50 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="mb-16 text-center">
          <div className="inline-flex items-center border border-gray-200 px-4 py-2 bg-white/80 backdrop-blur-sm text-xs font-bold uppercase tracking-widest text-amber-600 mb-6 rounded-md shadow-sm">
            <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
            Technology Stack
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-6 leading-tight uppercase">
            Modern Tools, <br />
            <span className="text-amber-600">Powerful Results</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We strategically combine cutting-edge technologies with proven best practices to delive <span className="font-semibold">speed, exceptional quality, and cost-efficiency.</span> Our carefully curated tech stack ensures <span className="font-semibold">scalability, reliability, and future-proof architecture</span> tailored to your unique business needs.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* LEFT: Floating Tech Icons - Responsive Grid */}
          <div className="w-full">
            {/* Desktop: Floating Layout */}
            <div 
              className="hidden lg:block relative h-[600px] overflow-hidden"
              onMouseMove={handleMouseMove}
            >
              
              {/* Decorative dotted lines background */}
              <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" style={{ background: 'url("data:image/svg+xml,%3Csvg width=\'50\' height=\'50\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'5\' cy=\'5\' r=\'1.5\' fill=\'%235b21b6\'/%3E%3C/svg%3E) repeat' }} />
              
              {/* Floating Icons Container */}
              <div className="absolute inset-0">
                {technologies.map((tech, idx) => {
                  const animSettings = getRandomAnimation(idx);
                  const isHovered = hoveredIdx === idx;
                  return (
                    <div
                      key={idx}
                      className="absolute cursor-pointer transition-all duration-300"
                      style={{
                        left: tech.x,
                        top: tech.y,
                        animation: `randomFloat${idx} ${animSettings.duration}s ease-in-out infinite`,
                        animationDelay: `${animSettings.delay}s`,
                        transform: isHovered ? 'scale(1.3)' : 'scale(1)',
                        filter: isHovered ? 'brightness(1.2) drop-shadow(0 0 20px rgba(255, 255, 255, 0.5))' : 'brightness(1)',
                        zIndex: isHovered ? 50 : 10,
                      }}
                      onMouseEnter={() => setHoveredIdx(idx)}
                      onMouseLeave={() => setHoveredIdx(null)}
                    >
                      {/* Dynamic Glow on Hover */}
                      {isHovered && (
                        <div 
                          className="absolute inset-0 rounded-full opacity-40 blur-2xl transition-all"
                          style={{
                            background: `radial-gradient(circle, ${tech.borderColor}88, transparent 70%)`,
                            animation: 'pulse-glow 1.5s ease-in-out infinite',
                          }} 
                        />
                      )}
                      
                      {/* Icon with Shape Variety */}
                      <div 
                        className={`${tech.size} bg-white flex items-center justify-center shadow-md transition-all relative`}
                        style={{
                          ...getShapeStyle(tech.shape),
                          border: `3px solid ${tech.borderColor}`,
                          boxShadow: isHovered 
                            ? `0 0 25px ${tech.borderColor}80, 0 0 40px ${tech.borderColor}50, 0 10px 20px rgba(0, 0, 0, 0.2)` 
                            : `0 0 15px ${tech.borderColor}40, 0 4px 6px rgba(0, 0, 0, 0.1)`,
                          transform: isHovered ? `rotate(${10 * Math.sin(idx)}deg) scale(1.05)` : 'rotate(0deg)',
                        }}
                      >
                        <img 
                          src={tech.logo}
                          alt={tech.name}
                          className="w-full h-full object-contain filter p-2"
                          style={{
                            animation: isHovered ? `spin-icon 2s linear infinite` : 'none',
                            filter: isHovered ? 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.8))' : 'none',
                          }}
                          onError={(e) => {
                            if (e.target) {
                              e.target.style.display = 'none';
                              const parent = e.target.parentElement;
                              if (parent) {
                                parent.innerHTML = '';
                                const text = document.createElement('span');
                                text.textContent = tech.name.substring(0, tech.name.includes('.') ? tech.name.indexOf('.') : 2).toUpperCase();
                                text.style.fontSize = '14px';
                                text.style.fontWeight = 'bold';
                                text.style.color = tech.borderColor;
                                parent.appendChild(text);
                              }
                            }
                          }}
                        />
                      </div>
                      
                      {/* Enhanced Tooltip */}
                      {isHovered && (
                        <div 
                          className="absolute -bottom-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap shadow-xl z-50 animate-bounce"
                          style={{
                            animation: 'tooltip-pop 0.3s ease-out',
                            background: `linear-gradient(135deg, ${tech.borderColor}, #1f2937)`,
                          }}
                        >
                          {tech.name}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900" style={{ background: tech.borderColor }}></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile/Tablet: Categorized Grid Layout */}
            <div className="lg:hidden space-y-6">
              {/* Category Buttons */}
              <div className="flex flex-wrap gap-2 justify-center mb-6">
                {['Frontend & Web', 'Backend & Databases', 'DevOps & Cloud', 'Design & UI', 'Tools & Version Control'].map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 ${
                      selectedCategory === category
                        ? 'bg-blue-600 text-white shadow-lg scale-105'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* Selected Category Technologies Display */}
              {(() => {
                const categoryTechs = technologies.filter(t => t.category === selectedCategory);
                return (
                  <div key={selectedCategory} className="space-y-3">
                    {/* Category Header */}
                    <div 
                      className="flex items-center gap-3 px-1"
                      style={{
                        animation: `slideInLeft 0.6s ease-out both`,
                      }}
                    >
                      <div 
                        className="h-1 flex-grow"
                        style={{
                          background: `linear-gradient(90deg, ${categoryTechs[0]?.borderColor || '#3b82f6'}, transparent)`,
                        }}
                      />
                      <h4 className="text-sm sm:text-base font-bold text-gray-800 whitespace-nowrap">
                        {selectedCategory}
                      </h4>
                      <div 
                        className="h-1 flex-grow"
                        style={{
                          background: `linear-gradient(90deg, transparent, ${categoryTechs[categoryTechs.length - 1]?.borderColor || '#3b82f6'})`,
                        }}
                      />
                    </div>

                    {/* Category Icons Grid */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                      {categoryTechs.map((tech, idx) => {
                        const isHovered = hoveredIdx === technologies.indexOf(tech);
                        const delayValue = idx * 0.08;
                        
                        return (
                          <div
                            key={tech.name}
                            className="flex flex-col items-center cursor-pointer group"
                            onMouseEnter={() => setHoveredIdx(technologies.indexOf(tech))}
                            onMouseLeave={() => setHoveredIdx(null)}
                            style={{
                              animation: `fadeInUp 0.5s ease-out ${delayValue}s both`,
                            }}
                          >
                            {/* Icon Container */}
                            <div 
                              className={`w-12 h-12 sm:w-16 sm:h-16 bg-white flex items-center justify-center shadow-md transition-all relative group-hover:shadow-xl group-hover:scale-110`}
                              style={{
                                ...getShapeStyle(tech.shape),
                                border: `2px sm:3px solid ${tech.borderColor}`,
                                boxShadow: isHovered 
                                  ? `0 0 20px ${tech.borderColor}80, 0 0 30px ${tech.borderColor}50, 0 8px 16px rgba(0, 0, 0, 0.15)` 
                                  : `0 0 10px ${tech.borderColor}40, 0 2px 4px rgba(0, 0, 0, 0.1)`,
                                transform: isHovered ? 'scale(1.15) translateY(-2px)' : 'scale(1)',
                              }}
                            >
                              <img 
                                src={tech.logo}
                                alt={tech.name}
                                className="w-8 h-8 sm:w-10 sm:h-10 object-contain filter"
                                style={{
                                  animation: isHovered ? `spin-icon 2s linear infinite` : 'none',
                                }}
                                onError={(e) => {
                                  if (e.target) {
                                    e.target.style.display = 'none';
                                    const parent = e.target.parentElement;
                                    if (parent) {
                                      parent.innerHTML = '';
                                      const text = document.createElement('span');
                                      text.textContent = tech.name.substring(0, tech.name.includes('.') ? tech.name.indexOf('.') : 2).toUpperCase();
                                      text.style.fontSize = '10px';
                                      text.style.fontWeight = 'bold';
                                      text.style.color = tech.borderColor;
                                      parent.appendChild(text);
                                    }
                                  }
                                }}
                              />
                            </div>
                            
                            {/* Label for mobile */}
                            <span className="text-xs font-semibold text-gray-700 mt-1.5 text-center leading-tight opacity-80 group-hover:opacity-100 transition-opacity">
                              {tech.name.split(' ')[0]}
                            </span>

                            {/* Tooltip on hover */}
                            {isHovered && (
                              <div 
                                className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-2 py-1 rounded text-xs font-bold whitespace-nowrap shadow-lg z-50"
                                style={{
                                  background: `linear-gradient(135deg, ${tech.borderColor}, #1f2937)`,
                                  animation: 'tooltip-pop 0.3s ease-out',
                                }}
                              >
                                {tech.name}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* RIGHT: technology Logo */}
          <div className="flex items-center justify-center h-[400px] lg:h-[600px]">
            <div className="relative w-full max-w-sm lg:max-w-2xl h-full flex items-center justify-center">
              <img 
                src="/img/tech.png" 
                alt="Garud Stacks technology expertise logo"
                className="max-w-xs lg:max-w-full max-h-80 lg:max-h-full object-contain filter drop-shadow-lg hover:drop-shadow-2xl transition-all duration-300 hover:scale-105 lg:hover:scale-110"
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        ${[...Array(26)].map((_, i) => {
          const randomX = Math.sin(i * 0.7) * 8;
          const randomY = Math.cos(i * 0.4) * 10;
          return `
            @keyframes randomFloat${i} {
              0%, 100% {
                transform: translateY(0px) translateX(0px);
              }
              20% {
                transform: translateY(${-randomY}px) translateX(${randomX}px);
              }
              40% {
                transform: translateY(${randomY}px) translateX(${randomX * 0.5}px);
              }
              60% {
                transform: translateY(${randomY * 0.5}px) translateX(${-randomX}px);
              }
              80% {
                transform: translateY(${-randomY * 0.5}px) translateX(${-randomX * 0.5}px);
              }
            }
          `;
        }).join('')}
        
        @keyframes spin-icon {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            transform: scale(1);
            opacity: 0.4;
          }
          50% { 
            transform: scale(1.2);
            opacity: 0.6;
          }
        }
        
        @keyframes tooltip-pop {
          0% {
            opacity: 0;
            transform: translate(-50%, 10px) scale(0.5);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, 0px) scale(1);
          }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes slideInLeft {
          0% {
            opacity: 0;
            transform: translateX(-30px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
